import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, ImageOff } from "lucide-react";
import { API } from "@/config";

function Lightbox({ photo, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="gallery-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[60] bg-obsidian/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
    >
      <button
        data-testid="lightbox-close-btn"
        onClick={onClose}
        className="absolute top-5 right-5 text-white/80 hover:text-white p-2"
        aria-label="Cerrar"
      >
        <X className="w-7 h-7" />
      </button>
      <figure className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={photo.url}
          alt={photo.title || "Trabajo de uñas"}
          className="w-full max-h-[75vh] object-contain rounded-xl"
        />
        {photo.title && (
          <figcaption className="text-center text-cream/90 font-display text-xl mt-4 italic">
            {photo.title}
          </figcaption>
        )}
      </figure>
    </div>
  );
}

export default function Gallery() {
  const [photos, setPhotos] = useState(null);
  const [selected, setSelected] = useState(null);
  const close = useCallback(() => setSelected(null), []);

  useEffect(() => {
    axios
      .get(`${API}/photos`)
      .then((res) => setPhotos(res.data))
      .catch(() => setPhotos([]));
  }, []);

  return (
    <section id="galeria" data-testid="gallery-section" className="py-16 sm:py-24 lg:py-32 sunset-glow">
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs sm:text-sm tracking-[0.3em] uppercase text-gold font-medium">02 · Galería</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-obsidian mt-4">
              Trabajos que hablan por sí solos
            </h2>
            <p className="text-sm sm:text-base text-taupe mt-4 leading-relaxed">
              Una selección de mis sets favoritos. Cada foto es una clienta real,
              cada diseño una pequeña obra de arte.
            </p>
          </div>
          <p className="text-xs text-taupe italic">Toca cualquier foto para verla en grande</p>
        </div>

        <div className="mt-12">
          {photos === null ? (
            <div className="columns-2 sm:columns-3 lg:columns-4 gap-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="mb-5 h-56 rounded-2xl bg-sand/60 animate-pulse" />
              ))}
            </div>
          ) : photos.length === 0 ? (
            <div data-testid="gallery-empty" className="text-center py-20 text-taupe">
              <ImageOff className="w-10 h-10 mx-auto mb-4 text-sand" />
              <p className="font-display text-2xl">Próximamente nuevos diseños</p>
            </div>
          ) : (
            <div data-testid="gallery-grid" className="columns-2 sm:columns-3 lg:columns-4 gap-5">
              {photos.map((p) => (
                <button
                  key={p.id}
                  data-testid={`gallery-photo-${p.id}`}
                  onClick={() => setSelected(p)}
                  className="group relative mb-5 w-full break-inside-avoid rounded-2xl overflow-hidden border border-sand bg-creamcard focus:outline-none focus:ring-2 focus:ring-gold"
                >
                  <img
                    src={p.url}
                    alt={p.title || "Trabajo de uñas de ISI'S Nails"}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {p.title && (
                    <span className="absolute bottom-0 inset-x-0 p-4 text-left text-cream text-sm font-medium translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                      {p.title}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {selected && <Lightbox photo={selected} onClose={close} />}
    </section>
  );
}
